import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2SignInSplitCombineResponse,
  AdminV2SignInSplitKeep,
  AdminV2SignInSplitSeparateResponse,
  AdminV2SignInSplitSeparationResponse,
} from "@/types/adminV2/crmSignInSplit";

const personUrl = (personId: string, action: string) =>
  `/admin/v2/crm/people/${encodeURIComponent(personId)}/${action}`;

const changedCrmTags = (personIds: string[]) => [
  { type: "AdminV2CrmPeople" as const, id: "LIST" },
  ...personIds.map((id) => ({ type: "AdminV2CrmPeople" as const, id })),
  { type: "AdminV2CrmDuplicates" as const, id: "LIST" },
  { type: "AdminV2CrmIdentityReview" as const, id: "LIST" },
  { type: "AdminV2CrmTimeline" as const, id: "LIST" },
  { type: "AdminV2AuditLog" as const, id: "LIST" },
];

/**
 * Combine and separate for a Landing contact who signed in with a different
 * email. Both need CRM edit, contact PII and access to all organisations.
 */
export const buildAdminV2CrmSignInSplitEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  combineAdminV2CrmSignInSplit: builder.mutation<
    AdminV2SignInSplitCombineResponse,
    { itemId: string; keep: AdminV2SignInSplitKeep }
  >({
    query: ({ itemId, keep }) => ({
      url: `/admin/v2/crm/identity-review/identity_issue/${encodeURIComponent(itemId)}/combine`,
      method: "POST",
      data: { keep },
    }),
    invalidatesTags: (result) =>
      changedCrmTags(
        result ? [result.keptPersonId, result.combinedPersonId] : [],
      ),
  }),

  getAdminV2CrmSignInSplitSeparation: builder.query<
    AdminV2SignInSplitSeparationResponse,
    { personId: string }
  >({
    query: ({ personId }) => ({ url: personUrl(personId, "sign-in-split") }),
    providesTags: (_result, _error, { personId }) => [
      { type: "AdminV2CrmPeople", id: personId },
    ],
    keepUnusedDataFor: 10,
  }),

  separateAdminV2CrmSignInSplit: builder.mutation<
    AdminV2SignInSplitSeparateResponse,
    { personId: string }
  >({
    query: ({ personId }) => ({
      url: personUrl(personId, "separate"),
      method: "POST",
      data: {},
    }),
    invalidatesTags: (result, _error, { personId }) =>
      changedCrmTags(
        result
          ? [personId, result.formPersonId, result.signedInPersonId]
          : [personId],
      ),
  }),
});
